import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { API_URL } from '../../../utils/api';
import { markCommentAsOK, undoMarkCommentAsOK } from '../utils/feedbackUtils';
import { calculateSectionTotals as globalCalculateSectionTotals, calculateItemTotal } from '../domain/calculations';

/**
 * useEstimatView - Facade hook för EstimatView
 * 
 * Kapslar in ALL state management och logik för EstimatView
 * Används både för kundvy och embedded mode för byggkonsulter
 * 
 * @param {string} leadIdParam - Lead ID (kan vara override)
 * @param {string} estimatIdParam - Estimat ID (kan vara override)
 * @param {boolean} embedded - Om vyn är embedded i EstimatPage
 * @param {Function} onFeedbackSent - Callback när feedback skickas
 * 
 * @returns {Object} - Strukturerad API med data, actions, status, etc.
 */
const useEstimatView = (leadIdParam, estimatIdParam, embedded = false, onFeedbackSent = null) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const notifications = useNotifications();
  
  // ==========================================
  // 📦 STATE - Data
  // ==========================================
  const [lead, setLead] = useState(null);
  const [estimatData, setEstimatData] = useState(null);
  const [procurementData, setProcurementData] = useState(null);
  const [builderProposals, setBuilderProposals] = useState(null);
  const [homeVisitProposals, setHomeVisitProposals] = useState(null);
  
  // ==========================================
  // 📦 STATE - Comments & Feedback
  // ==========================================
  const [comments, setComments] = useState({});
  const [savedComments, setSavedComments] = useState({});
  const [commentsRaw, setCommentsRaw] = useState({});
  // Spårar att vi bara synkar BK:s drafts från DB en gång per session
  const initialBkDraftsSynced = useRef(false);
  // 🔥 Ladda savedReplies från localStorage vid mount (per estimat)
  const [savedReplies, setSavedReplies] = useState(() => {
    if (!estimatIdParam) return {};
    try {
      const stored = localStorage.getItem(`estimat_replies_${estimatIdParam}`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  // 🔥 Initiera newReplies med savedReplies så textfälten visas med sparad text
  const [newReplies, setNewReplies] = useState(() => {
    if (!estimatIdParam) return {};
    try {
      const stored = localStorage.getItem(`estimat_replies_${estimatIdParam}`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [inlineReplies, setInlineReplies] = useState({});
  const [overallFeedback, setOverallFeedback] = useState('');
  // Baseline = vad textarean innehöll vid mount (laddat från DB).
  // Används för att avgöra om kunden FAKTISKT har ändrat texten innan vi re-postar den.
  // Förhindrar dubletter där stale state pushas till tråden vid varje "Skicka feedback".
  const [overallFeedbackBaseline, setOverallFeedbackBaseline] = useState('');
  const [overallReply, setOverallReply] = useState('');
  // Prioriterade frågor från första-estimatmailet — visas som egna textareas
  // ovanför Övergripande feedback i FeedbackSubmitForm. Lagras under reserverade
  // comments-keys __budget__ och __prioritering__.
  const [budgetFeedback, setBudgetFeedback] = useState('');
  const [budgetFeedbackBaseline, setBudgetFeedbackBaseline] = useState('');
  const [prioriteringFeedback, setPrioriteringFeedback] = useState('');
  const [prioriteringFeedbackBaseline, setPrioriteringFeedbackBaseline] = useState('');
  
  // ==========================================
  // 📦 STATE - Meeting Request
  // ==========================================
  const [wantsMeeting, setWantsMeeting] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingSlots, setMeetingSlots] = useState([]);
  
  // ==========================================
  // 📦 STATE - Proposals
  // ==========================================
  const [acceptingProposal, setAcceptingProposal] = useState('');
  const [acceptingHomeVisitProposal, setAcceptingHomeVisitProposal] = useState('');
  const [showTimeProposalReply, setShowTimeProposalReply] = useState({});
  const [showHomeVisitReply, setShowHomeVisitReply] = useState({});
  const [timeProposalReplyText, setTimeProposalReplyText] = useState({});
  const [homeVisitReplyText, setHomeVisitReplyText] = useState({});
  
  // ==========================================
  // 📦 STATE - Followup Notes (byggkonsult only)
  // ==========================================
  const [followupNotes, setFollowupNotes] = useState('');
  const [followupNotesSaved, setFollowupNotesSaved] = useState(true);
  
  // ==========================================
  // 📦 STATE - UI State
  // ==========================================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingFollowupNotes, setSavingFollowupNotes] = useState(false);
  const [sendingTimeProposalReply, setSendingTimeProposalReply] = useState(false);
  const [sendingHomeVisitReply, setSendingHomeVisitReply] = useState(false);
  const [isCustomerView, setIsCustomerView] = useState(false);
  const [showComments, setShowComments] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    projektering: true,
    byggoffert: true,
    sidoentreprenorer: true,
    inkopFastInredning: true
  });
  const [expandedFeedbackItems, setExpandedFeedbackItems] = useState({});
  const [expandedTimeProposal, setExpandedTimeProposal] = useState(true); // Visa expanderad som standard
  const [expandedHomeVisitProposal, setExpandedHomeVisitProposal] = useState(true); // Visa expanderad som standard
  const [expandedBookedHomeVisit, setExpandedBookedHomeVisit] = useState(false); // Minimerad som standard
  const [expandedBookedFollowupMeeting, setExpandedBookedFollowupMeeting] = useState(false); // Minimerad som standard
  const [feedbackSent, setFeedbackSent] = useState(false);
  
  // ==========================================
  // 📦 STATE - Modals & Toasts
  // ==========================================
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showImportant, setShowImportant] = useState(() => {
    try {
      return localStorage.getItem('hide_estimat_important') !== '1';
    } catch (_) {
      return true;
    }
  });
  // Intro-meddelandet dismissas per unikt meddelande (baserat på createdAt)
  // så att ett nytt meddelande från BK alltid visas även om kunden tidigare kryssat bort ett
  const [dismissedIntroKey, setDismissedIntroKey] = useState(() => {
    try {
      return localStorage.getItem('hide_estimat_intro_key') || '';
    } catch (_) {
      return '';
    }
  });
  const [pendingAction, setPendingAction] = useState(null);
  
  // ==========================================
  // 📦 STATE - Tracking
  // ==========================================
  const [hasLoggedView, setHasLoggedView] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  
  // ==========================================
  // 🛠️ UTILITY FUNCTIONS
  // ==========================================
  
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE');
  }, []);
  
  const generateMeetingOptions = useCallback(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      const weekday = d.getDay();
      if (weekday >= 3 && weekday <= 5) {
        const dateStr = d.toLocaleDateString('sv-SE', { weekday: 'short', day: '2-digit', month: '2-digit' });
        options.push(`${dateStr} kl 11:00–12:00`);
        options.push(`${dateStr} kl 14:00–15:00`);
      }
      if (options.length >= 20) break;
    }
    return options;
  }, []);
  
  const calculateTotal = useCallback(() => {
    if (!estimatData?.estimatKategorier) return 0;

    let total = 0;
    let byggoffertTotal = 0;
    let etableringBase = 0;

    Object.entries(estimatData.estimatKategorier).forEach(([categoryKey, kategori]) => {
      if (kategori && typeof kategori === 'object') {
        Object.entries(kategori).forEach(([itemName, item]) => {
          // Använd calculateItemTotal för korrekt beräkning inkl subItems
          const itemTotal = calculateItemTotal(item);
          if (itemTotal > 0) {
            total += itemTotal;

            if (categoryKey === 'byggoffert') {
              byggoffertTotal += itemTotal;
              if (!itemName.includes('Altan') && !itemName.includes('Ställning')) {
                etableringBase += itemTotal;
              }
            }
          }
        });
      }
    });

    if (byggoffertTotal > 0) {
      const etablering = etableringBase * 0.1;
      total += etablering;
    }

    const rotAntal = estimatData?.projectInfo?.rotAvdrag || 0;
    const rotBelopp = estimatData?.projectInfo?.rotBelopp || 0;
    const rotAvdragTotal = rotAntal * rotBelopp;
    total -= rotAvdragTotal;

    return total;
  }, [estimatData]);
  
  // 🎯 ANVÄND GLOBAL BERÄKNINGSFUNKTION - ingen duplicerad kod!
  const calculateSectionTotals = useCallback(() => {
    return globalCalculateSectionTotals(
      estimatData?.estimatKategorier,
      estimatData?.projectInfo,
      false, // isRotBeloppEditable används bara i edit-mode
      estimatData?.baspaketAndelar
    );
  }, [estimatData]);
  
  // ==========================================
  // 🔄 ACTIVITY LOGGING
  // ==========================================
  
  const logActivity = useCallback(async (activityType, details = {}) => {
    if (!user || user.role !== 'kund' || !estimatIdParam) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/estimat/${estimatIdParam}/log-activity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ activityType, details })
      });
    } catch (error) {
      console.warn(`⚠️ Kunde inte logga aktivitet ${activityType}:`, error);
    }
  }, [user, estimatIdParam]);
  
  // ==========================================
  // 💾 FOLLOWUP NOTES (byggkonsult only)
  // ==========================================
  
  const saveFollowupNotes = useCallback(async () => {
    if (!estimatIdParam || !user || user.role !== 'byggkonsult') return;
    if (followupNotesSaved) return;
    
    setSavingFollowupNotes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/followup-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: followupNotes })
      });
      
      if (response.ok) {
        setFollowupNotesSaved(true);
        
        // 🔄 Hämta uppdaterad estimatData för att visa den nya anteckningen direkt
        const estimatResponse = await fetch(`${API_URL}/api/estimat/${estimatIdParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (estimatResponse.ok) {
          const updatedEstimat = await estimatResponse.json();
          setEstimatData(updatedEstimat);
        }
        
        notifications.showToast('Anteckning sparad!', 'success', 2000);
      }
    } catch (error) {
      console.error('❌ Fel vid sparande av uppföljningsanteckningar:', error);
      notifications.showError('Kunde inte spara', error.message || 'Okänt fel');
    } finally {
      setSavingFollowupNotes(false);
    }
  }, [estimatIdParam, user, followupNotes, followupNotesSaved, notifications]);
  
  // ==========================================
  // 💬 COMMENT MANAGEMENT
  // ==========================================

  // Synka BK:s opublicerade drafts från DB till comments+savedComments vid
  // initial laddning så textarean visar texten och "Skicka alla"-knappen
  // syns. Annars måste BK börja om från noll efter sidladdning.
  useEffect(() => {
    if (initialBkDraftsSynced.current) return;
    const bkResponses = estimatData?.byggkonsultResponses;
    if (!bkResponses) return;

    const drafts = {};
    Object.entries(bkResponses).forEach(([key, val]) => {
      if (val && typeof val === 'object' && val.draft === true && val.text) {
        drafts[key] = val.text;
      }
    });

    if (Object.keys(drafts).length > 0) {
      setComments(prev => ({ ...prev, ...drafts }));
      setSavedComments(prev => ({ ...prev, ...drafts }));
    }
    initialBkDraftsSynced.current = true;
  }, [estimatData]);

  const handleCommentChange = useCallback((categoryKey, itemName, comment) => {
    const key = `${categoryKey}_${itemName}`;
    setComments(prev => ({ ...prev, [key]: comment }));
  }, []);
  
  const toggleCommentField = useCallback((categoryKey, itemName) => {
    const key = `${categoryKey}_${itemName}`;
    setShowComments(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  
  const hasUnsavedChanges = useCallback((categoryKey, itemName) => {
    const key = `${categoryKey}_${itemName}`;
    const currentComment = comments[key] || '';
    const rawComment = commentsRaw[key];
    const savedComment = typeof rawComment === 'object' ? (rawComment?.text || '') : (rawComment || '');
    return currentComment.trim() !== savedComment.trim();
  }, [comments, commentsRaw]);
  
  const isSavedLocally = useCallback((categoryKey, itemName) => {
    const key = `${categoryKey}_${itemName}`;
    return Boolean(savedComments[key]);
  }, [savedComments]);
  
  const handleSaveComment = useCallback(async (categoryKey, itemName) => {
    const key = `${categoryKey}_${itemName}`;
    const comment = comments[key];
    if (!comment || !comment.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'feedback', comments: { [key]: comment.trim() }, autoSave: true })
      });
      if (!response.ok) throw new Error('Kunde inte spara');
      const data = await response.json();
      if (data.estimat) {
        setEstimatData(data.estimat);
        if (data.estimat?.customerResponse?.comments) setCommentsRaw(data.estimat.customerResponse.comments);
      }
      setSavedComments(prev => { const u = {...prev}; delete u[key]; return u; });
      // Radens namn som kunden ser ("Etablering & admin"), inte nyckeln
      // ("etablering_admin") — det är det BK läser i aktivitetskortet.
      // Etableringsraden ritas av EtableringRow och finns inte bland
      // estimatets rader, så dess namn står här.
      const SPECIALRADER = { etablering_admin: 'Etablering, avfall & admin' };
      const rad = data.estimat?.estimatKategorier?.[categoryKey]?.[itemName];
      const radnamn = rad?.label || SPECIALRADER[itemName] || itemName.replace(/_/g, ' ');
      // Hela texten: aktivitetsloggen är enda backupen om feedbacken tappas
      logActivity('comment_draft', { section: radnamn, draftLength: comment.trim().length, draftText: comment.trim() });
    } catch (error) {
      console.error('Fel vid auto-save kommentar:', error);
    }
  }, [comments, estimatIdParam, logActivity, setCommentsRaw]);

  // Auto-save övergripande feedback som draft till DB
  const handleSaveOverallFeedback = useCallback(async () => {
    const text = overallFeedback?.trim();
    if (!text || !estimatIdParam) return;
    // Hoppa över om kunden inte har ändrat texten sedan sidan laddades (annars re-pushas baseline-texten)
    if (text === overallFeedbackBaseline.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'feedback', overallFeedback: text, comments: {}, autoSave: true })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.estimat) {
          setEstimatData(data.estimat);
          if (data.estimat?.customerResponse?.comments) setCommentsRaw(data.estimat.customerResponse.comments);
        }
      }
    } catch (err) {
      console.error('Fel vid auto-save övergripande feedback:', err);
    }
  }, [overallFeedback, overallFeedbackBaseline, estimatIdParam, setCommentsRaw]);

  // Auto-save budget-svar som draft (key __budget__). Samma pattern som
  // handleSaveOverallFeedback men POSTar via comments-key istället för
  // overallFeedback-fältet — backend hanterar __budget__ via samma
  // arbiträr-key-loop i handleCustomerResponse.
  const handleSaveBudgetFeedback = useCallback(async () => {
    const text = budgetFeedback?.trim();
    if (!text || !estimatIdParam) return;
    if (text === budgetFeedbackBaseline.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'feedback', comments: { __budget__: text }, autoSave: true })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.estimat) {
          setEstimatData(data.estimat);
          if (data.estimat?.customerResponse?.comments) setCommentsRaw(data.estimat.customerResponse.comments);
        }
      }
    } catch (err) {
      console.error('Fel vid auto-save budget-svar:', err);
    }
  }, [budgetFeedback, budgetFeedbackBaseline, estimatIdParam, setCommentsRaw]);

  // Auto-save prioritering-svar (key __prioritering__).
  const handleSavePrioriteringFeedback = useCallback(async () => {
    const text = prioriteringFeedback?.trim();
    if (!text || !estimatIdParam) return;
    if (text === prioriteringFeedbackBaseline.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'feedback', comments: { __prioritering__: text }, autoSave: true })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.estimat) {
          setEstimatData(data.estimat);
          if (data.estimat?.customerResponse?.comments) setCommentsRaw(data.estimat.customerResponse.comments);
        }
      }
    } catch (err) {
      console.error('Fel vid auto-save prioritering-svar:', err);
    }
  }, [prioriteringFeedback, prioriteringFeedbackBaseline, estimatIdParam, setCommentsRaw]);

  // Ta bort en sparad kundkommentar från backend
  const handleDeleteComment = useCallback(async (categoryKey, itemName) => {
    const key = `${categoryKey}_${itemName}`;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'feedback', comments: {}, deleteCommentKeys: [key], autoSave: true })
      });
      if (!response.ok) throw new Error('Kunde inte ta bort');
      const data = await response.json();
      if (data.estimat) {
        setEstimatData(data.estimat);
        if (data.estimat?.customerResponse?.comments) setCommentsRaw(data.estimat.customerResponse.comments);
      }
      // Rensa lokalt
      setComments(prev => { const u = {...prev}; delete u[key]; return u; });
      setSavedComments(prev => { const u = {...prev}; delete u[key]; return u; });
      setShowComments(prev => { const u = {...prev}; delete u[key]; return u; });
    } catch (error) {
      console.error('Fel vid borttagning av kommentar:', error);
    }
  }, [estimatIdParam, setCommentsRaw]);

  // Redigera en specifik thread-entry i BK:s svar (Slack-style edit)
  const handleEditByggkonsultEntry = useCallback(async (commentKey, entryIndex, newText) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/byggkonsult-response/edit-entry`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentKey, entryIndex, newText })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Kunde inte redigera svar');
      }
      const data = await res.json();
      if (data.estimat) {
        setEstimatData(data.estimat);
      }
    } catch (error) {
      console.error('Fel vid redigering av thread-entry:', error);
      notifications.showError('Kunde inte redigera', error.message);
    }
  }, [estimatIdParam, notifications]);

  // Auto-spara byggkonsult-kommentar som draft till servern (proaktiv kommentar på rad).
  // Triggas vid blur. Drafts publiceras inte till kund förrän BK klickar
  // "Skicka alla" — så BK kan ändra texten utan att kund ser den.
  const handleSaveByggkonsultComment = useCallback(async (categoryKey, itemName) => {
    const key = `${categoryKey}_${itemName}`;
    const comment = comments[key];
    if (!comment || !comment.trim()) {
      // Tom textarea vid blur — gör inget (ingen toast)
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/byggkonsult-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          commentKey: key,
          response: comment.trim(),
          autoSave: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kunde inte spara kommentar');
      }

      const data = await response.json();

      // Uppdatera estimatData med hela estimat (inkl. nya byggkonsultResponses)
      if (data.estimat) {
        setEstimatData(data.estimat);
      }

      // Behåll comments[key] så textarean visar texten — BK ska kunna
      // återgå och ändra. Markera även i savedComments så "Skicka alla"-
      // knappen visas och BK vet att de behöver publicera.
      setSavedComments(prev => ({ ...prev, [key]: comment.trim() }));
    } catch (error) {
      console.error('❌ Fel vid auto-save byggkonsult-kommentar:', error);
    }
  }, [comments, estimatIdParam]);
  
  const handleSendComment = useCallback(async (categoryKey = '', itemName = '') => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      // Om enskild kommentar anges, spara den som utkast först
      if (categoryKey && itemName) {
        const key = `${categoryKey}_${itemName}`;
        const comment = comments[key];
        if (!comment || !comment.trim()) {
          notifications.showError('Ingen kommentar', 'Du måste skriva en kommentar innan du skickar.');
          return;
        }
        // Spara som utkast först
        await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'feedback', comments: { [key]: comment.trim() }, autoSave: true })
        });
      }

      // Publicera alla utkast
      const bodyData = {};

      /**
       * ⚠️ Skicka ALLTID med en icke-tom text. Jämför INTE mot baslinjen här.
       *
       * Här låg en baslinjekontroll — texten skickades bara om den skilde sig
       * från vad sidan laddades med — för att slippa dubbletter i tråden. Men
       * autosparningen (`handleSaveOverallFeedback`) tar emot `data.estimat` i
       * svaret och flyttar därmed baslinjen till kundens egen text. Trycker
       * kunden sedan "Skicka feedback" eller "Godkänn" ser kontrollen ingen
       * ändring och UTESLUTER texten. Kunden ser sin text på skärmen och tror
       * att den gick med; byggkonsulten får ett svar utan innehåll.
       *
       * Mätt i produktion 2026-10-06: fem kunder hade skrivit en kommentar och
       * därefter godkänt estimatet. Ingen av texterna nådde fram — tre av dem
       * var riktiga frågor (vad som ingår, önskan om hembesök, en fråga om
       * framdragning) som aldrig besvarades. Calle Halvarsson #45820 samma dag:
       * feedbacken registrerades utan en enda kommentar, och bara ett
       * säkerhetsnät i procurementController gjorde att den syntes alls.
       *
       * Dubbletter är inte vårt problem att lösa här: publishCustomerComments
       * gör `if (newText !== existingText.trim())` före den lägger till i
       * tråden, och publicerar ett oförändrat utkast genom att bara rensa
       * draft-flaggan. Backend är auktoriteten på om texten ändrats.
       */
      if (overallFeedback && overallFeedback.trim()) {
        bodyData.overallFeedback = overallFeedback.trim();
      }
      if (budgetFeedback && budgetFeedback.trim()) {
        bodyData.budgetFeedback = budgetFeedback.trim();
      }
      if (prioriteringFeedback && prioriteringFeedback.trim()) {
        bodyData.prioriteringFeedback = prioriteringFeedback.trim();
      }
      if (wantsMeeting) {
        bodyData.meetingRequest = {
          note: meetingNotes.trim(),
          slots: meetingSlots
        };
      }

      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/publish-customer-comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Kunde inte skicka kommentar');

      // Uppdatera state med data från backend
      if (data.estimat) {
        setEstimatData(data.estimat);
        if (data.estimat?.customerResponse?.comments) {
          setCommentsRaw(data.estimat.customerResponse.comments);
        }
      }

      setSavedComments({});
      setFeedbackSent(true);
      notifications.showSuccess('Skickat!', 'Dina kommentarer har skickats till byggkonsulten.');
      logActivity('comment_sent', {});

    } catch (error) {
      console.error('❌ Fel vid skickande av kommentar:', error);
      notifications.showError('Fel', error.message || 'Kunde inte skicka kommentar');
    } finally {
      setSubmitting(false);
    }
    // notifications utelämnad med avsikt: NotificationContext memoiserar inte
    // sitt value-objekt, så det byts vid varje toast. Vi anropar bara dess
    // metoder (showError/showSuccess) och läser aldrig värden ur det.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, savedComments, estimatIdParam, logActivity, overallFeedback, overallFeedbackBaseline, budgetFeedback, budgetFeedbackBaseline, prioriteringFeedback, prioriteringFeedbackBaseline, wantsMeeting, meetingNotes, meetingSlots]);
  
  const handleSaveReply = useCallback(async (commentKey) => {
    const reply = newReplies[commentKey];
    if (!reply || !reply.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'feedback', comments: { [commentKey]: reply.trim() }, autoSave: true })
      });
      if (!response.ok) throw new Error('Kunde inte spara');
      const data = await response.json();
      if (data.estimat) {
        setEstimatData(data.estimat);
        if (data.estimat?.customerResponse?.comments) setCommentsRaw(data.estimat.customerResponse.comments);
      }
      // Rensa textarean (newReplies) men behåll i savedReplies så
      // "Skicka feedback"-knappen visas tills kunden klickat Skicka.
      // Den sparade texten visas i thread-vyn som "(utkast)" ändå.
      setNewReplies(prev => { const u = {...prev}; delete u[commentKey]; return u; });
      setSavedReplies(prev => ({ ...prev, [commentKey]: reply.trim() }));
    } catch (error) {
      console.error('Fel vid auto-save svar:', error);
    }
  }, [newReplies, estimatIdParam, setCommentsRaw]);
  
  const handleSendAllReplies = useCallback(async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      // Bygg ETT comments-objekt med alla sparade svar så backend bara skickar
      // ETT mail + EN notis till BK — tidigare loopade vi och fick N utskick.
      const commentsPayload = {};
      for (const [commentKey, reply] of Object.entries(savedReplies)) {
        if (reply && reply.trim()) {
          commentsPayload[commentKey] = reply.trim();
        }
      }

      if (Object.keys(commentsPayload).length === 0) {
        return;
      }

      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'feedback',
          comments: commentsPayload
        })
      });

      if (!response.ok) throw new Error('Fel vid skickande av svar');

      const data = await response.json();
      const updatedEstimat = data.estimat || data;

      if (updatedEstimat) {
        setEstimatData(updatedEstimat);
        if (updatedEstimat?.customerResponse?.comments) {
          setCommentsRaw(updatedEstimat.customerResponse.comments);
        }
      }

      // Rensa lokala svar
      setSavedReplies({});
      setNewReplies({});

      setFeedbackSent(true);
      notifications.showSuccess('Alla svar skickade!', 'Dina svar har skickats till byggkonsulten.');
      logActivity('comment_sent', { replyCount: Object.keys(commentsPayload).length });

    } catch (error) {
      console.error('❌ Fel vid skickande av svar:', error);
      notifications.showError('Fel', error.message || 'Kunde inte skicka svar');
    } finally {
      setSubmitting(false);
    }
    // notifications utelämnad med avsikt: NotificationContext memoiserar inte
    // sitt value-objekt, så det byts vid varje toast. Vi anropar bara dess
    // metoder (showError/showSuccess) och läser aldrig värden ur det.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedReplies, estimatIdParam, logActivity, setCommentsRaw]);
  
  const handleSendInlineReply = useCallback(async (commentKey) => {
    const reply = inlineReplies[commentKey];
    if (!reply || !reply.trim()) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'feedback',
          comments: { [commentKey]: reply.trim() }
        })
      });
      
      if (!response.ok) throw new Error('Kunde inte skicka svar');
      
      // Hämta uppdaterat estimat från servern
      const data = await response.json();
      const updatedEstimat = data.estimat || data;
      
      // Uppdatera lokal state
      setEstimatData(updatedEstimat);
      if (updatedEstimat?.customerResponse?.comments) {
        setCommentsRaw(updatedEstimat.customerResponse.comments);
      }
      
      setInlineReplies(prev => ({ ...prev, [commentKey]: '' }));
      setFeedbackSent(true);
      notifications.showSuccess('Svar skickat!', 'Ditt svar har skickats till byggkonsulten.');
      logActivity('comment_sent', { commentKey });
      
    } catch (error) {
      console.error('❌ Fel vid skickande av inline-svar:', error);
      notifications.showError('Fel', error.message || 'Kunde inte skicka svar');
    } finally {
      setSubmitting(false);
    }
    // notifications utelämnad med avsikt: NotificationContext memoiserar inte
    // sitt value-objekt, så det byts vid varje toast. Vi anropar bara dess
    // metoder (showError/showSuccess) och läser aldrig värden ur det.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineReplies, estimatIdParam, logActivity, setCommentsRaw]);
  
  const handleCustomerMarkAsOK = useCallback(async (commentKey) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Använd generisk utility-funktion
      const updatedEstimat = await markCommentAsOK(estimatIdParam, commentKey, 'customer', token);
      
      // Uppdatera lokal state med nya data från servern
      setEstimatData(updatedEstimat);
      if (updatedEstimat?.customerResponse?.comments) {
        setCommentsRaw(updatedEstimat.customerResponse.comments);
      }
      
      // Rensa lokala "replies" för denna kommentar
      setNewReplies(prev => {
        const updated = { ...prev };
        delete updated[commentKey];
        return updated;
      });
      setSavedReplies(prev => {
        const updated = { ...prev };
        delete updated[commentKey];
        return updated;
      });
      
      // Visa toast
      notifications.showToast('Markerat som OK!', 'success');
      
    } catch (error) {
      console.error('❌ Fel vid markering som OK:', error);
      notifications.showToast(error.message || 'Kunde inte markera som OK', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [estimatIdParam, notifications, setCommentsRaw]);

  // Ångra kundens "Markerad som OK" — gör feedbacken aktiv igen
  const handleCustomerUndoMarkAsOK = useCallback(async (commentKey) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      const updatedEstimat = await undoMarkCommentAsOK(estimatIdParam, commentKey, token);

      setEstimatData(updatedEstimat);
      // customerResponse.comments kan ha tappat nyckeln helt — sätt alltid från servern
      setCommentsRaw(updatedEstimat?.customerResponse?.comments || {});

      notifications.showToast('Markeringen ångrad', 'success');
    } catch (error) {
      console.error('❌ Fel vid ångra av markering:', error);
      notifications.showToast(error.message || 'Kunde inte ångra markeringen', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [estimatIdParam, notifications, setCommentsRaw, setEstimatData]);

  // ==========================================
  // 🎯 PROPOSALS MANAGEMENT
  // ==========================================
  
  const handleAcceptProposal = useCallback(async (proposal) => {
    try {
      setAcceptingProposal(proposal.displayText);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/time-proposals/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ acceptedProposal: proposal })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Kunde inte acceptera tidsförslag');
      }
      
      // ✅ Uppdatera state om backend returnerar uppdaterad data
      if (data.estimat) {
        setEstimatData(data.estimat);
        // ✅ Uppdatera builderProposals med den nya datan som innehåller customerResponse.status === 'accepted'
        if (data.estimat.estimatFollowup) {
          setBuilderProposals(data.estimat.estimatFollowup);
        }
      }

      notifications.showToast(`Uppföljningsförslag accepterat: ${proposal.displayText}`, 'success', 4000);
      // Note: proposal_accepted is not a valid activity type for customer activity logging
      
    } catch (error) {
      console.error('❌ Fel vid acceptans av tidsförslag:', error);
      notifications.showError('Kunde inte acceptera förslag', error.message);
    } finally {
      setAcceptingProposal('');
    }
    // notifications utelämnad med avsikt: NotificationContext memoiserar inte
    // sitt value-objekt, så det byts vid varje toast. Vi anropar bara dess
    // metoder (showError/showSuccess) och läser aldrig värden ur det.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatIdParam, logActivity]);
  
  const handleTimeProposalReply = useCallback(async (proposalIndex) => {
    const replyText = timeProposalReplyText[proposalIndex];
    if (!replyText || !replyText.trim()) return;
    
    try {
      setSendingTimeProposalReply(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/time-proposals/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyText.trim() })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Kunde inte skicka svar');
      }
      
      // ✅ Uppdatera state med data från backend
      if (data.estimat) {
        setEstimatData(data.estimat);
        // ✅ Uppdatera builderProposals så att sektionen döljs (status blir 'replied')
        if (data.estimat.estimatFollowup) {
          setBuilderProposals(data.estimat.estimatFollowup);
        }
      }
      
      notifications.showToast('Ditt svar har skickats till byggkonsulten', 'success', 3000);
      setTimeProposalReplyText(prev => ({ ...prev, [proposalIndex]: '' }));
      setShowTimeProposalReply(prev => ({ ...prev, [proposalIndex]: false }));
      
      // Note: proposal_reply_sent is not a valid activity type for customer activity logging
      
    } catch (error) {
      console.error('❌ Fel vid skickande av svar:', error);
      notifications.showError('Kunde inte skicka svar', error.message);
    } finally {
      setSendingTimeProposalReply(false);
    }
    // notifications utelämnad med avsikt: NotificationContext memoiserar inte
    // sitt value-objekt, så det byts vid varje toast. Vi anropar bara dess
    // metoder (showError/showSuccess) och läser aldrig värden ur det.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeProposalReplyText, estimatIdParam, logActivity]);
  
  const handleAcceptHomeVisitProposal = useCallback(async (proposal) => {
    if (!procurementData?._id) {
      notifications.showError('Kunde inte acceptera förslag', 'Procurement ID saknas');
      return;
    }
    
    try {
      setAcceptingHomeVisitProposal(proposal.displayText);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/procurement/${procurementData._id}/home-visit-proposals/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ acceptedProposal: proposal })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Kunde inte acceptera hembesöksförslag');
      }
      
      // ✅ Uppdatera procurement data om backend returnerar den
      if (data.procurement) {
        setProcurementData(data.procurement);
      }
      
      notifications.showToast(`Hembesöksförslag accepterat: ${proposal.displayText}`, 'success', 4000);
      setHomeVisitProposals(null);
      
    } catch (error) {
      console.error('❌ Fel vid acceptans av hembesöksförslag:', error);
      notifications.showError('Kunde inte acceptera förslag', error.message);
    } finally {
      setAcceptingHomeVisitProposal('');
    }
  }, [procurementData, notifications]);
  
  const handleHomeVisitReply = useCallback(async (proposalIndex) => {
    const replyText = homeVisitReplyText[proposalIndex];
    if (!replyText || !replyText.trim()) return;
    
    if (!procurementData?._id) {
      notifications.showError('Kunde inte skicka svar', 'Procurement ID saknas');
      return;
    }
    
    try {
      setSendingHomeVisitReply(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/procurement/${procurementData._id}/home-visit-proposals/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyText.trim() })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Kunde inte skicka svar');
      }
      
      // ✅ Uppdatera state med data från backend
      if (data.procurement) {
        setProcurementData(data.procurement);
      }
      
      notifications.showToast('Ditt svar har skickats till byggkonsulten', 'success', 3000);
      setHomeVisitReplyText(prev => ({ ...prev, [proposalIndex]: '' }));
      setShowHomeVisitReply(prev => ({ ...prev, [proposalIndex]: false }));
      
      // Note: homevisit_reply_sent is not a valid activity type for customer activity logging
      
    } catch (error) {
      console.error('❌ Fel vid skickande av svar:', error);
      notifications.showError('Kunde inte skicka svar', error.message);
    } finally {
      setSendingHomeVisitReply(false);
    }
  }, [homeVisitReplyText, procurementData, notifications]);

  // Kund skickar hembesöksförslag
  const [submittingHomeVisitProposals, setSubmittingHomeVisitProposals] = useState(false);
  
  const handleSubmitHomeVisitProposals = useCallback(async (proposals, message) => {
    if (!procurementData?._id) {
      notifications.showError('Kunde inte skicka förslag', 'Procurement ID saknas');
      throw new Error('Procurement ID saknas');
    }
    
    if (!proposals || proposals.length === 0) {
      notifications.showError('Kunde inte skicka förslag', 'Välj minst en tid');
      throw new Error('Inga tider valda');
    }
    
    try {
      setSubmittingHomeVisitProposals(true);
      const token = localStorage.getItem('token');
      
      // Formatera proposals till rätt format
      const formattedProposals = proposals.map(slot => {
        // Parsa displayText till date och time om möjligt
        // Format exempel: "Måndag 15 dec kl 14:00"
        return {
          displayText: slot,
          date: '', // Backend kan parsa detta vid behov
          time: ''
        };
      });
      
      const response = await fetch(`${API_URL}/api/procurement/${procurementData._id}/customer-home-visit-proposals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          proposals: formattedProposals, 
          message: message || '' 
        })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Kunde inte skicka förslag');
      }
      
      // Uppdatera state med data från backend
      if (data.procurement) {
        setProcurementData(data.procurement);
      }
      
      notifications.showToast('Ditt förslag har skickats till byggkonsulten', 'success', 3000);
      
    } catch (error) {
      console.error('Fel vid skickande av hembesöksförslag:', error);
      notifications.showError('Kunde inte skicka förslag', error.message);
      throw error;
    } finally {
      setSubmittingHomeVisitProposals(false);
    }
  }, [procurementData, notifications]);
  
  // ==========================================
  // ✅ ESTIMAT ACTIONS (Approve/Decline)
  // ==========================================
  
  const handleConfirmAction = useCallback(async () => {
    setShowConfirmModal(false);
    if (pendingAction) {
      await executeEstimatAction(pendingAction);
      setPendingAction(null);
    }
    // executeEstimatAction utelämnad med avsikt: den definieras på rad ~1056,
    // alltså EFTER den här callbacken. Att referera den i beroendelistan läses
    // under renderingen innan const tilldelats — alltså ReferenceError.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction]);
  
  const executeEstimatAction = useCallback(async (action) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      let data;

      if (action === 'feedback') {
        // Publicera alla utkast-kommentarer via publish-endpoint
        const bodyData = {};
        // Ingen baslinjejämförelse — se motiveringen i publishCustomerComments
        // ovan. Backend avgör om texten ändrats; vi ska bara leverera den.
        if (overallFeedback && overallFeedback.trim()) {
          bodyData.overallFeedback = overallFeedback.trim();
        }
        if (budgetFeedback && budgetFeedback.trim()) {
          bodyData.budgetFeedback = budgetFeedback.trim();
        }
        if (prioriteringFeedback && prioriteringFeedback.trim()) {
          bodyData.prioriteringFeedback = prioriteringFeedback.trim();
        }
        if (wantsMeeting) {
          bodyData.meetingRequest = {
            note: meetingNotes.trim(),
            slots: meetingSlots
          };
        }

        const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/publish-customer-comments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(bodyData)
        });

        data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Kunde inte skicka feedback');
      } else {
        /**
         * Godkänn/Avböj — customer-response direkt.
         *
         * ⚠️ DEN HÄR raden var den som kostade mest. Den skickade en TOM STRÄNG
         * när texten matchade baslinjen, och backend hoppar över tom text. En
         * kund som skrev en fråga och sedan tryckte Godkänn fick alltså sin
         * text slängd — autosparningen hade redan flyttat baslinjen.
         *
         * Fem kunder i produktion hade gjort precis det (mätt 2026-10-06), och
         * tre av texterna var frågor som aldrig besvarades. Ett godkännande med
         * en fråga är fortfarande en fråga.
         */
        const bodyData = {
          action,
          comments: savedComments,
          overallFeedback: overallFeedback.trim()
        };

        if (wantsMeeting) {
          bodyData.meetingRequest = {
            note: meetingNotes.trim(),
            slots: meetingSlots
          };
        }

        const response = await fetch(`${API_URL}/api/estimat/${estimatIdParam}/customer-response`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(bodyData)
        });

        data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Kunde inte skicka svar');
      }
      
      // ✅ Uppdatera state med den returnerade datan från backend
      if (data.estimat) {
        setEstimatData(data.estimat);
        if (data.estimat?.customerResponse?.comments) {
          setCommentsRaw(data.estimat.customerResponse.comments);
        }
      }
      
      // Skapa rätt meddelande baserat på action och användarroll
      let title, message;
      const isConsultant = user?.role === 'byggkonsult';
      
      if (action === 'approved') {
        title = 'Estimat godkänt!';
        message = isConsultant 
          ? 'Du har godkänt estimatet åt kunden. Kunden har fått ett meddelande om detta.'
          : 'Du har godkänt estimatet. Byggkonsulten kommer att kontakta dig snart.';
      } else if (action === 'declined') {
        title = 'Estimat nekat!';
        message = isConsultant
          ? 'Du har nekat estimatet åt kunden. Kunden har fått ett meddelande om detta.'
          : 'Du har nekat estimatet. Byggkonsulten kommer att kontakta dig snart.';
      } else if (action === 'feedback') {
        title = 'Feedback skickad!';
        message = isConsultant
          ? 'Din feedback har skickats till kunden.'
          : 'Din feedback har skickats till byggkonsulten som kommer att svara på dina frågor.';
      }
      
      setSavedComments({});
      setComments({});
      
      // Note: estimat_feedback is not a valid activity type, skip logging for feedback
      if (action !== 'feedback') {
        logActivity(`estimat_${action}`);
      }
      
      // Visa GlobalModal med onClose callback för att uppdatera sidan
      notifications.showSuccess(title, message, () => {
        if (onFeedbackSent) {
          onFeedbackSent();
        }
      });
      
    } catch (error) {
      console.error('❌ Fel vid estimat-action:', error);
      notifications.showError('Fel', error.message || 'Kunde inte skicka svar');
    } finally {
      setSubmitting(false);
    }
    // notifications utelämnad, se ovan. user?.role tillagd i listan nedan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedComments, overallFeedback, overallFeedbackBaseline, budgetFeedback, budgetFeedbackBaseline, prioriteringFeedback, prioriteringFeedbackBaseline, wantsMeeting, meetingNotes, meetingSlots, estimatIdParam, logActivity, onFeedbackSent, user?.role]);
  
  const handleEstimatAction = useCallback(async (action) => {
    // Kolla om det finns OSPARADE kommentarer (i textfält men inte sparade lokalt)
    // VIKTIGT: Jämför mot savedComments (lokalt sparade) FÖRST, annars mot commentsRaw (från DB)
    const hasUnsavedComments = Object.keys(comments).some(key => {
      const currentComment = comments[key] || '';
      
      // Hämta senast sparade värdet (lokalt eller från DB)
      let savedComment = '';
      if (savedComments[key]) {
        // Om det finns i savedComments (lokalt sparad), använd det
        savedComment = savedComments[key];
      } else {
        // Annars, använd det från databasen (commentsRaw)
        const rawComment = commentsRaw[key];
        savedComment = typeof rawComment === 'object' ? (rawComment?.text || '') : (rawComment || '');
      }
      
      return currentComment.trim() !== savedComment.trim();
    });
    
    if (hasUnsavedComments) {
      setShowConfirmModal(true);
      setPendingAction(action);
    } else {
      await executeEstimatAction(action);
    }
  }, [comments, commentsRaw, savedComments, executeEstimatAction]);
  
  // ==========================================
  // 💾 PERSIST SAVED REPLIES TO LOCALSTORAGE
  // ==========================================

  useEffect(() => {
    if (!estimatIdParam) return;
    try {
      if (Object.keys(savedReplies).length > 0) {
        localStorage.setItem(`estimat_replies_${estimatIdParam}`, JSON.stringify(savedReplies));
      } else {
        localStorage.removeItem(`estimat_replies_${estimatIdParam}`);
      }
    } catch (e) {
      console.warn('Kunde inte spara svar till localStorage:', e);
    }
  }, [savedReplies, estimatIdParam]);

  // ==========================================
  // 📥 DATA FETCHING
  // ==========================================

  // Byter man estimat måste datan hämtas om.
  //
  // hasFetchedData är en engångsspärr mot dubbelhämtning. Den gjorde att vyn
  // fastnade på det FÖRST öppnade estimatet: kunden kunde växla mellan
  // parallella estimat och såg samma siffror hela tiden, eftersom effekten
  // returnerade direkt trots att id:t ändrats.
  useEffect(() => {
    setHasFetchedData(false);
  }, [estimatIdParam, leadIdParam]);

  // Osparade svarsutkast hör till det estimat de skrevs i.
  //
  // savedReplies och newReplies initieras från localStorage med en
  // useState-initierare, och en sådan körs BARA vid första monteringen. Vid
  // växling låg därför texten kvar och såg ut att tillhöra det nya estimatet —
  // kunden kunde tro att en kommentar fanns på båda, fast den bara skulle
  // skickas till det ena. Läs om utkasten för det estimat som faktiskt visas.
  useEffect(() => {
    const lasUtkast = () => {
      if (!estimatIdParam) return {};
      try {
        const sparat = localStorage.getItem(`estimat_replies_${estimatIdParam}`);
        return sparat ? JSON.parse(sparat) : {};
      } catch {
        return {};
      }
    };
    const utkast = lasUtkast();
    setSavedReplies(utkast);
    setNewReplies(utkast);
    setInlineReplies({});
  }, [estimatIdParam]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetchedData) return;
      setHasFetchedData(true);
      
      try {
        const token = localStorage.getItem('token');
        
        if (estimatIdParam) {
          // Access via estimatId
          const isActualCustomer = user?.role === 'kund';
          setIsCustomerView(isActualCustomer);
          
          const estimatResponse = await fetch(`${API_URL}/api/estimat/${estimatIdParam}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (!estimatResponse.ok) {
            throw new Error(`Kunde inte hämta estimat: ${estimatResponse.status}`);
          }
          
          const estimat = await estimatResponse.json();
          setEstimatData(estimat);
          
          // Log customer view
          if (isActualCustomer && !hasLoggedView) {
            try {
              await fetch(`${API_URL}/api/estimat/${estimatIdParam}/log-activity`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  activityType: 'page_view',
                  details: {}
                })
              });
              setHasLoggedView(true);
            } catch (logError) {
              console.warn('⚠️ Kunde inte logga kundöppning:', logError);
            }
          }
          
          // Load builder proposals
          if (estimat.estimatFollowup && estimat.estimatFollowup.proposals) {
            setBuilderProposals(estimat.estimatFollowup);
          }
          
          // Load home visit proposals (customer only)
          if (isActualCustomer) {
            try {
              const procurementResponse = await fetch(`${API_URL}/api/procurement/customer/my-procurement`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              if (procurementResponse.ok) {
                const procurementData = await procurementResponse.json();
                const procurements = Array.isArray(procurementData) ? procurementData : [procurementData];
                
                const relevantProcurement = procurements.find(p => {
                  if (!p) return false;
                  const procurementLeadId = p.leadId?._id || p.leadId;
                  const estimatLeadId = estimat.leadId?._id || estimat.leadId;
                  return procurementLeadId === estimatLeadId;
                });
                
                setProcurementData(relevantProcurement);
                
                if (relevantProcurement?.homeVisitProposals?.proposals) {
                  setHomeVisitProposals(relevantProcurement.homeVisitProposals);
                }
              }
            } catch (procurementError) {
              console.error('❌ Fel vid hämtning av hembesöksförslag:', procurementError);
            }
          }
          
          // Load existing comments
          if (estimat.customerResponse && estimat.customerResponse.comments) {
            const entries = estimat.customerResponse.comments || {};
            setCommentsRaw(entries);
            const textMap = Object.fromEntries(
              Object.entries(entries).map(([k, v]) => [k, (typeof v === 'object' ? (v?.text || '') : (v || ''))])
            );
            setComments(textMap);
            
            // Load overall feedback and meeting request
            try {
              const overall = entries.__overall__;
              if (overall) {
                const initialText = typeof overall === 'object' ? (overall.text || '') : (overall || '');
                setOverallFeedback(initialText);
                setOverallFeedbackBaseline(initialText);
              }
              const budget = entries.__budget__;
              if (budget) {
                const initialText = typeof budget === 'object' ? (budget.text || '') : (budget || '');
                setBudgetFeedback(initialText);
                setBudgetFeedbackBaseline(initialText);
              }
              const prioritering = entries.__prioritering__;
              if (prioritering) {
                const initialText = typeof prioritering === 'object' ? (prioritering.text || '') : (prioritering || '');
                setPrioriteringFeedback(initialText);
                setPrioriteringFeedbackBaseline(initialText);
              }
              const mr = entries.__meetingRequest__;
              if (mr) {
                setWantsMeeting(true);
                if (typeof mr === 'object') {
                  setMeetingNotes(mr.note || mr.text || '');
                  setMeetingSlots(Array.isArray(mr.slots) ? mr.slots : []);
                } else {
                  setMeetingNotes(String(mr));
                }
              }
            } catch (_) {}
          }
          
          // Load lead data
          if (estimat.leadId) {
            if (typeof estimat.leadId === 'object') {
              setLead(estimat.leadId);
            } else {
              console.warn('⚠️ Kund kan inte hämta lead-data direkt');
              setLead({
                customerInfo: estimat.leadId.customerInfo || {},
                projectInfo: estimat.leadId.projectInfo || {}
              });
            }
          }
          
        } else if (leadIdParam) {
          // Admin access via leadId
          setIsCustomerView(false);
          
          const estimatResponse = await fetch(`${API_URL}/api/estimat/lead/${leadIdParam}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          // finns: false = inget estimat än; behandla som "hittades inte"
          // i stället för att lägga ett tomt objekt i estimatData.
          const estimatBody = estimatResponse.ok ? await estimatResponse.json() : null;
          if (estimatBody && estimatBody._id && estimatBody.finns !== false) {
            const estimat = estimatBody;
            setEstimatData(estimat);

            // Load comments and lead data similar to above...
            if (estimat.customerResponse && estimat.customerResponse.comments) {
              const entries = estimat.customerResponse.comments || {};
              setCommentsRaw(entries);
              const textMap = Object.fromEntries(
                Object.entries(entries).map(([k, v]) => [k, (typeof v === 'object' ? (v?.text || '') : (v || ''))])
              );
              setComments(textMap);
            }
            
            // Try loading from customer data
            if (estimat.customerId) {
              try {
                const customerResponse = await fetch(`${API_URL}/api/users/customer/${estimat.customerId}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (customerResponse.ok) {
                  const customerData = await customerResponse.json();
                  const leadCompatibleData = {
                    customerInfo: {
                      name: `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim(),
                      email: customerData.email,
                      phone: customerData.phone || '',
                      phone2: customerData.phone2 || ''
                    },
                    projectInfo: {
                      address: customerData.customerProjectInfo?.address || '',
                      city: customerData.customerProjectInfo?.city || '',
                      postCode: customerData.customerProjectInfo?.postCode || '',
                      projectType: customerData.customerProjectInfo?.projectType || '',
                      size: customerData.customerProjectInfo?.size || '',
                      budget: customerData.customerProjectInfo?.budget || '',
                      propertyType: customerData.customerProjectInfo?.propertyType || '',
                      floor: customerData.customerProjectInfo?.floor || '',
                      elevator: customerData.customerProjectInfo?.elevator || ''
                    }
                  };
                  setLead(leadCompatibleData);
                }
              } catch (customerError) {
                console.error('❌ Fel vid hämtning av kunddata:', customerError);
              }
            } else {
              // Active lead
              try {
                const leadResponse = await fetch(`${API_URL}/api/leads/${leadIdParam}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (leadResponse.ok) {
                  const leadData = await leadResponse.json();
                  setLead(leadData);
                }
              } catch (leadError) {
                console.error('❌ Fel vid hämtning av lead-data:', leadError);
              }
            }
          } else {
            console.warn('⚠️ Inget estimat hittades');
            setError('Inget estimat hittades för detta lead');
          }
        }
        
      } catch (error) {
        console.error('💥 Fel vid hämtning av data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (leadIdParam || estimatIdParam) {
      fetchData();
    } else {
      console.error('❌ Inget leadId eller estimatId tillgängligt');
      setError('Inget ID tillgängligt');
      setLoading(false);
    }
  }, [leadIdParam, estimatIdParam, hasFetchedData, user, hasLoggedView]);
  
  // ==========================================
  // 💾 AUTO-SAVE FOLLOWUP NOTES
  // ==========================================
  
  useEffect(() => {
    if (!followupNotesSaved && user?.role === 'byggkonsult') {
      const timer = setTimeout(() => {
        saveFollowupNotes();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [followupNotes, followupNotesSaved, user, saveFollowupNotes]);
  
  // ==========================================
  // ⌨️ KEYBOARD SHORTCUTS (Ctrl+S)
  // ==========================================
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (user?.role === 'byggkonsult' && !followupNotesSaved) {
          saveFollowupNotes();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user, followupNotesSaved, saveFollowupNotes]);
  
  // ==========================================
  // 📤 LOAD FOLLOWUP NOTES
  // ==========================================
  
  useEffect(() => {
    if (!estimatData || user?.role !== 'byggkonsult') return;
    
    const today = new Date().toISOString().split('T')[0];
    const noteId = `followup-${today}`;
    const dynamicNotes = estimatData?.intervjuFrågor?.dynamicNotes || [];
    const todaysNote = dynamicNotes.find(note => note.id === noteId);
    
    if (todaysNote && todaysNote.content) {
      setFollowupNotes(todaysNote.content);
      setFollowupNotesSaved(true);
    }
  }, [estimatData, user]);
  
  // ==========================================
  // 📤 RETURN - Strukturerad API
  // ==========================================
  
  return {
    // Data
    data: {
      lead,
      estimatData,
      procurementData,
      builderProposals,
      homeVisitProposals,
      comments,
      savedComments,
      commentsRaw,
      newReplies,
      savedReplies,
      inlineReplies,
      overallFeedback,
      overallReply,
      budgetFeedback,
      prioriteringFeedback,
      wantsMeeting,
      meetingNotes,
      meetingSlots,
      followupNotes,
      expandedSections,
      expandedFeedbackItems,
      showComments,
      isCustomerView,
      user,
      timeProposalReplyText,
      homeVisitReplyText,
    },
    
    // Derived
    derived: {
      totalAmount: calculateTotal(),
      sectionTotals: calculateSectionTotals(),
      hasUnsavedComments: Object.keys(savedComments).length > 0 ||
        Object.values(estimatData?.customerResponse?.comments || {}).some(v => v && typeof v === 'object' && v.draft),
      hasUnsavedReplies: Object.keys(savedReplies).length > 0,
      canSubmitEstimat: !submitting && estimatData?.customerResponse?.action !== 'approved' && estimatData?.customerResponse?.action !== 'declined',
    },
    
    // Actions
    actions: {
      handleCommentChange,
      toggleCommentField,
      handleSaveComment,
      handleSaveOverallFeedback,
      handleSaveBudgetFeedback,
      handleSavePrioriteringFeedback,
      handleDeleteComment,
      handleSaveByggkonsultComment,
      handleEditByggkonsultEntry,
      handleSendComment,
      handleSaveReply,
      handleSendAllReplies,
      handleSendInlineReply,
      handleCustomerMarkAsOK,
      handleCustomerUndoMarkAsOK,
      handleAcceptProposal,
      handleTimeProposalReply,
      handleAcceptHomeVisitProposal,
      handleHomeVisitReply,
      handleSubmitHomeVisitProposals,
      handleEstimatAction,
      handleConfirmAction,
      saveFollowupNotes,
      hasUnsavedChanges,
      isSavedLocally,
      formatDate,
      generateMeetingOptions,
      navigate,
      logActivity,
    },
    
    // Status
    status: {
      loading,
      error,
      submitting,
      savingFollowupNotes,
      sendingTimeProposalReply,
      sendingHomeVisitReply,
      submittingHomeVisitProposals,
      followupNotesSaved,
      feedbackSent,
      showMeetingModal,
      showConfirmModal,
      showImportant,
      dismissedIntroKey,
      acceptingProposal,
      acceptingHomeVisitProposal,
      expandedTimeProposal,
      expandedHomeVisitProposal,
      expandedBookedHomeVisit,
      expandedBookedFollowupMeeting,
      showTimeProposalReply,
      showHomeVisitReply,
    },
    
    // Setters
    setters: {
      setComments,
      setOverallFeedback,
      setBudgetFeedback,
      setPrioriteringFeedback,
      setOverallReply,
      setWantsMeeting,
      setMeetingNotes,
      setMeetingSlots,
      setFollowupNotes,
      setFollowupNotesSaved,
      setExpandedSections,
      setExpandedFeedbackItems,
      setExpandedTimeProposal,
      setExpandedHomeVisitProposal,
      setExpandedBookedHomeVisit,
      setExpandedBookedFollowupMeeting,
      setShowComments,
      setShowMeetingModal,
      setShowConfirmModal,
      setShowImportant,
      setDismissedIntroKey,
      setNewReplies,
      setSavedReplies,
      setInlineReplies,
      setShowTimeProposalReply,
      setShowHomeVisitReply,
      setTimeProposalReplyText,
      setHomeVisitReplyText,
    },
  };
};

export default useEstimatView;

